import NextArrow from '@assets/svgs/next-arrow.svg';
import NotificationIcon from '@components/Icon/Notification';
import { ProfileButton, ProfileMenu } from '@components/ProfileMenu';
import { Badge, Box, IconButton, styled, Typography, useTheme } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import MuiLink from '@mui/material/Link';
import React from 'react';
import { useLocation, Link } from 'react-router-dom';

interface BreadcrumbItem {
    label: string;
    href?: string;
    value?: string;
}

interface BreadcrumbsProps {
    items: BreadcrumbItem[];
    heading?: string;
}

const CustomBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-dot': {
        top: '2px',
        right: '5px',
    },
}));

export const BreadcrumbsComponent: React.FC<BreadcrumbsProps> = ({ items, heading }) => {
    const [profileMenuAnchor, setProfileMenuAnchor] = React.useState<null | HTMLElement>(null);
    const location = useLocation();
    const { pathname } = location;
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'start',
                padding: { xs: '0 8px', sm: '0 16px', md: '0 24px' },
                paddingTop: '16px',
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                backgroundColor: theme => theme.palette.neutralBlack.main,
            }}>
            <Box
                sx={{
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: { xs: '8px', sm: 0 },
                    borderBottom: theme => `2px solid ${theme.palette.mode === 'dark' ? theme.palette.border.main : theme.palette.gray.main}`,
                    paddingBottom: '13px',
                    marginBottom: '26px',
                    width: '100%',
                }}>
                <Box>
                    {heading && <Typography variant="displayXXSmall">{heading}</Typography>}
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        sx={{
                            '& .MuiBreadcrumbs-separator': {
                                display: 'none',
                            },
                        }}
                        style={{ marginTop: '5px' }}>
                        <MuiLink
                            component={Link}
                            to="/"
                            sx={{
                                maxHeight: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                color: theme =>
                                    pathname === '/' ? theme.palette.neutralWhite.main : theme.palette.neutralGray.main,
                            }}>
                            <Typography variant="labelSmallBold">Dashboard</Typography>
                            {pathname !== '/' && (
                                <img
                                    src={NextArrow}
                                    alt="Next Arrow"
                                    style={{ marginLeft: '6px', marginRight: '6px' }}
                                />
                            )}
                        </MuiLink>
                        {pathname
                            .split('/')
                            .slice(1)
                            .filter(segment => isNaN(Number(segment)))
                            .map((path, i) => (
                                <React.Fragment key={`1-${i}`}>
                                    {items.map((item, index) => {
                                        if (path === item.value) {
                                            if (pathname.split('/').slice(1).filter(segment => isNaN(Number(segment))).length === 1) {
                                                return (
                                                    <MuiLink
                                                        component={Link}
                                                        key={index}
                                                        to={item.href}
                                                        sx={{ maxHeight: '16px', display: 'flex' }}>
                                                        <Typography variant="labelSmallBold" color="textLink.main" data-testid="header-title">
                                                            {item.label}
                                                        </Typography>
                                                    </MuiLink>
                                                );
                                            } else {
                                                return (
                                                    <MuiLink
                                                        component={Link}
                                                        key={index}
                                                        to={
                                                            i !== 0 && window.location.pathname.includes(item.href)
                                                                ? window.location.pathname
                                                                : item.href
                                                        }
                                                        sx={{
                                                            color: theme =>
                                                                i !== 0 && window.location.pathname.includes(item.href)
                                                                    ? theme.palette.neutralWhite.main
                                                                    : theme.palette.neutralGray.main,
                                                            fontWeight:
                                                                i !== 0 && window.location.pathname.includes(item.href)
                                                                    ? 700
                                                                    : 500,
                                                            display: 'flex',
                                                            maxHeight: '16px',
                                                        }}>
                                                        <Typography variant="labelSmallBold">{item.label}</Typography>
                                                        {i < pathname.split('/').slice(1).filter(segment => isNaN(Number(segment))).length - 1 && (
                                                            <img
                                                                src={NextArrow}
                                                                alt="Next Arrow"
                                                                style={{ marginLeft: '6px', marginRight: '6px' }}
                                                            />
                                                        )}
                                                    </MuiLink>
                                                );
                                            }
                                        } else {
                                            return null;
                                        }
                                    })}
                                </React.Fragment>
                            ))}
                    </Breadcrumbs>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '8px', sm: '12px' }, alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
                    <IconButton
                        sx={{
                            background: theme => `${theme.palette.mode === 'dark' ? theme.palette.dashboard.main : theme.palette.primary.main}`,
                            p: { xs: '8px', sm: '12px' },
                            borderRadius: '100%',
                            height: { xs: '38px', sm: '54px' },
                            width: { xs: '38px', sm: '54px' },
                            '&:hover': {
                                backgroundColor: theme => theme.palette.backgroundPilsurface.main,
                            },
                        }}>
                        <CustomBadge variant="dot" color="error">
                            <Box component="span" sx={{ display: 'flex', '& svg, & img': { width: { xs: 20, sm: 30 }, height: { xs: 20, sm: 30 } } }}>
                                <NotificationIcon height={30} width={30} />
                            </Box>
                        </CustomBadge>
                    </IconButton>

                    <ProfileButton
                        onClick={event => setProfileMenuAnchor(event.currentTarget)}
                        isActive={Boolean(profileMenuAnchor)}
                    />

                    <ProfileMenu
                        anchorEl={profileMenuAnchor}
                        open={Boolean(profileMenuAnchor)}
                        onClose={() => setProfileMenuAnchor(null)}
                    />
                </Box>
            </Box>
        </Box>
    );
};
